import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Col } from "reactstrap";
import { useMediaQuery } from "react-responsive";
import { APIMS } from "../../../../../config/apims";
import "bulma/css/bulma.css";
import "../../../../../index.css";
import "../../../../../assets/css/module-ui.css";
import Swal from "sweetalert2";
import ModalAdd from "./modal/add";
import ModalUpdate from "./modal/update";
import ToastError from "../../../../NotificationToast/ToastError";
import ToastSuccess from "../../../../NotificationToast/ToastSuccess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendar,
  faMagnifyingGlass,
  faPenToSquare,
  faPlus,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { PulseLoader } from "react-spinners";
import Pagination from "../../../../components/pagination_new";

export default function TahunAjaran() {
  document.title = "Tahun Ajaran";

  const [getData, setGetData] = useState([]);
  const ascending = 1;
  const token = localStorage.getItem("token");

  const idUser = localStorage.getItem("id_admin")?.replace(/[|&;$%@"<>()+,]/g, "");

  const isTabletOrMobile = useMediaQuery({
    query: "(max-width: 780px)",
  });

  const [searchKeyword, setSearchKeyword] = useState("");
  const [tablePage, setTablePage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isFetching, setIsFetching] = useState(false);

  const [modalAdd, setModalAdd] = useState(false);
  const [modalUpdate, setModalUpdate] = useState(false);

  const [dataUpdate, setDataUpdate] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);

  const fetchParams = useMemo(
    () => ({
      headers: {
        Authorization: `${token}`,
        "Content-Type": "application/json",
      },
    }),
    [token]
  );

    const GetResponseData = useCallback(async () => {
      setSearchKeyword("");
      setTablePage(1);
      setIsFetching(true);

      try {
        const response = await APIMS.get(
          `/api/academic-years?page=1&limit=1000&ascending=${ascending}&search=`,
          fetchParams
        );

        if (response?.status === 200) {
          setGetData(response?.data?.data || []);
        }
      } catch (error) {
        ToastError.fire({
          icon: "error",
          title:
            error.response?.data?.message || "Gagal memuat data",
        });
      } finally {
        setIsFetching(false);
      }
    },
    [ascending, fetchParams]
  );

  useEffect(() => {
    GetResponseData();
  }, [GetResponseData]);

  const updateAcademicYearStatus = useCallback(
    async (academicYear, nextStatus) => {
      return APIMS.put(
        `/api/academic-years/${academicYear.id}`,
        {
          name_year: academicYear?.name_year || "",
          status: Number(nextStatus),
          user_id: idUser,
        },
        fetchParams
      );
    },
    [fetchParams, idUser]
  );

  const handleToggleStatus = async (row) => {
    if (statusUpdatingId !== null) {
      return;
    }

    const currentStatus =
      Number(row?.status) === 1 ? 1 : 0;

    const nextStatus = currentStatus === 1 ? 0 : 1;

    const deactivatedAcademicYears = [];

    setStatusUpdatingId(row?.id);

    try {
      /*
       * Jika tahun ajaran akan diaktifkan,
       * nonaktifkan dahulu seluruh tahun ajaran
       * lain yang masih aktif.
       */
      if (nextStatus === 1) {
        const activeAcademicYears = getData.filter(
          (item) =>
            item?.id !== row?.id &&
            Number(item?.status) === 1
        );

        for (const activeItem of activeAcademicYears) {
          await updateAcademicYearStatus(
            activeItem,
            0
          );

          deactivatedAcademicYears.push(
            activeItem
          );
        }
      }

      await updateAcademicYearStatus(
        row,
        nextStatus
      );

      ToastSuccess.fire({
        icon: "success",
        title:
          nextStatus === 1
            ? "Tahun ajaran berhasil diaktifkan"
            : "Tahun ajaran berhasil dinonaktifkan",
      });

      await GetResponseData(false);
    } catch (error) {
      /*
       * Mengembalikan status lama apabila proses
       * mengaktifkan tahun ajaran baru gagal.
       */
      if (
        nextStatus === 1 &&
        deactivatedAcademicYears.length > 0
      ) {
        await Promise.allSettled(
          deactivatedAcademicYears.map(
            (academicYear) =>
              updateAcademicYearStatus(
                academicYear,
                1
              )
          )
        );
      }

      await GetResponseData(false);

      ToastError.fire({
        icon: "error",
        title:
          error.response?.data?.message ||
          "Gagal mengubah status tahun ajaran",
      });
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const deleteById = async (id, name) => {
    Swal.fire({
      title: "Hapus data ini?",
      html: `<p class="sims-delete-description">Data <strong></strong> yang dihapus tidak dapat dikembalikan.</p>`,
      icon: "warning",
      customClass: {
        popup: "sims-delete-popup",
        icon: "sims-delete-icon",
        title: "sims-delete-title",
        actions: "sims-delete-actions",
        confirmButton: "sims-delete-confirm",
        cancelButton: "sims-delete-cancel",
        validationMessage: "sims-delete-validation",
      },
      buttonsStyling: false,
      showCancelButton: true,
      confirmButtonText: "Hapus data",
      cancelButtonText: "Batal",
      reverseButtons: true,
      focusCancel: true,

      didOpen: (popup) => {
        popup.querySelector(".sims-delete-description strong").textContent = name || "KKCA";
      },
      showLoaderOnConfirm: true,
      allowOutsideClick: () => !Swal.isLoading(),
      preConfirm: async () => {
        try {
          const response = await APIMS.delete(
            `/api/academic-years/${id}`,
            fetchParams
          );
  
          if (response.data.error) {
            throw new Error(response.data.message);
          }
  
          return response;
        } catch (error) {
          Swal.showValidationMessage(
            error.response?.data?.message || "Gagal menghapus data"
          );
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        ToastSuccess.fire({
          icon: "success",
          title: "Data berhasil dihapus",
        });
        GetResponseData();
      }
    });
  };

  const openUpdateModal = (row) => {
    setDataUpdate(row);
    setModalUpdate(true);
  };

  const nameCollator = useMemo(
    () => new Intl.Collator("id", { sensitivity: "base", numeric: true }),
    []
  );

  const filteredData = useMemo(() => {
    const keyword = (searchKeyword || "")
      .trim()
      .toLowerCase();

    const result = !keyword
      ? getData || []
      : (getData || []).filter((item) => {
          const nameYear = (
            item?.name_year || ""
          )
            .toString()
            .toLowerCase();

          const statusText =
            Number(item?.status) === 1
              ? "aktif"
              : "tidak aktif";

          return (
            nameYear.includes(keyword) ||
            statusText.includes(keyword)
          );
        });

    return [...result].sort((a, b) =>
      nameCollator.compare(
        (a?.name_year || "")
          .toString()
          .trim(),
        (b?.name_year || "")
          .toString()
          .trim()
      )
    );
  }, [getData, searchKeyword, nameCollator]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));

  useEffect(() => {
    setTablePage((page) => Math.min(page, totalPages));
  }, [totalPages]);

  const paginatedData = useMemo(() => {
    const safeRows = Math.max(1, rowsPerPage);
    const start = (tablePage - 1) * safeRows;
    return filteredData.slice(start, start + safeRows);
  }, [filteredData, tablePage, rowsPerPage]);


  return (
    <div className="body sims-page">
      {modalAdd && (
        <ModalAdd
          GetResponseData={GetResponseData}
          allAcademicYears={getData}
          show={modalAdd}
          onHide={() => setModalAdd(false)}
        />
      )}

      {modalUpdate && dataUpdate && (
        <ModalUpdate
          GetResponseData={GetResponseData}
          allAcademicYears={getData}
          dataUpdate={dataUpdate}
          show={modalUpdate}
          onHide={() => {
            setModalUpdate(false);
            setDataUpdate(null);
          }}
        />
      )}

      <div className="page-header-card">
        <div className="page-header-top d-flex">
          <div className="page-title-wrap">
            <span className="page-title-icon">
              <FontAwesomeIcon
                icon={faCalendar}
              />
            </span>

            <h4 className="page-title-text">
              Data Tahun Ajaran
            </h4>
          </div>

          <div className="ml-auto">
            <button
              type="button"
              onClick={() => setModalAdd(true)}
              className="btn btn-create mr-2 sims-header-button"
            >
              <FontAwesomeIcon icon={faPlus} />
              <span> {isTabletOrMobile ? "Tahun Ajaran" : "Tambah Tahun Ajaran"}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="body-content sims-content">
        {isFetching ? (
          <div className="sims-loading">
            <PulseLoader
              color="#3f5bea"
              size={12}
            />
          </div>
        ) : (
          <Col xl="12" sm="12" className="sims-column">
            <div className="sims-search-wrap mb-3">
              <FontAwesomeIcon
                icon={faMagnifyingGlass}
                className="sims-search-icon"
              />

              <input
                type="text"
                className="sims-search-input"
                placeholder="Cari tahun ajaran atau status..."
                value={searchKeyword}
                onChange={(event) => {
                  setSearchKeyword(event.target.value);
                  setTablePage(1);
                }}
              />
            </div>

            <div className="sims-table-card">
              <div className="sims-table-scroll">
                <table className="sims-table">
                  <thead>
                    <tr>
                      <th  className="sims-col-number">No</th>
                      <th>Tahun Ajaran</th>
                      <th className="sims-col-status" >Status</th>
                      <th className="sims-col-action">Aksi</th>
                    </tr>
                  </thead>

                  <tbody>
                    {paginatedData.length > 0 ? (
                      paginatedData.map(
                        (row, index) => {
                          const isActive =
                            Number(
                              row?.status
                            ) === 1;

                          const isUpdating =
                            statusUpdatingId ===
                            row?.id;

                          return (
                            <tr key={row?.id || index}>
                              <td className="sims-col-number">
                                <span className="sims-number-badge">
                                  {(tablePage - 1) * rowsPerPage + index + 1}
                                </span>
                              </td>

                              <td>
                                <span className="sims-text-bold">
                                  {row?.name_year || "-"}
                                </span>
                              </td>

                              <td className="sims-col-status">
                                <div className="sims-status-control">
                                  <label
                                    className={`sims-toggle ${
                                      isUpdating
                                        ? "is-loading"
                                        : ""
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={
                                        isActive
                                      }
                                      disabled={
                                        statusUpdatingId !==
                                        null
                                      }
                                      onChange={() =>
                                        handleToggleStatus(
                                          row
                                        )
                                      }
                                      aria-label={`Ubah status tahun ajaran ${
                                        row?.name_year ||
                                        ""
                                      }`}
                                    />

                                    <span className="sims-toggle-slider" />
                                  </label>

                                  <span
                                    className={`sims-status-label ${
                                      isActive
                                        ? "is-active"
                                        : "is-inactive"
                                    }`}
                                  >
                                    {isUpdating
                                      ? "Menyimpan..."
                                      : isActive
                                      ? "Aktif"
                                      : "Tidak Aktif"}
                                  </span>
                                </div>
                              </td>

                              <td className="sims-col-action">
                                <div className="sims-action-wrap">
                                  <button
                                    type="button"
                                    className="sims-action-button sims-action-edit"
                                    title="Edit tahun ajaran"
                                    onClick={() =>
                                      openUpdateModal(
                                        row
                                      )
                                    }
                                    disabled={
                                      statusUpdatingId !==
                                      null
                                    }
                                  >
                                    <FontAwesomeIcon
                                      icon={
                                        faPenToSquare
                                      }
                                    />
                                  </button>

                                  <button
                                    type="button"
                                    className="sims-action-button sims-action-delete"
                                    title="Hapus tahun ajaran"
                                    onClick={() =>
                                      deleteById(
                                        row?.id, row?.name_year
                                      )
                                    }
                                    disabled={
                                      statusUpdatingId !==
                                      null
                                    }
                                  >
                                    <FontAwesomeIcon
                                      icon={
                                        faTrash
                                      }
                                    />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        }
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan="4"
                          className="sims-empty-state"
                        >
                          Data tahun ajaran tidak ditemukan
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                
              </div>

              <Pagination
                totalRows={filteredData.length}
                tablePage={tablePage}
                rowsPerPage={rowsPerPage}
                onPageChange={setTablePage}
                onRowsPerPageChange={(value) => {
                  setRowsPerPage(value);
                  setTablePage(1);
                }}
              />
            </div>
          </Col>
        )}
      </div>
    </div>
  );
}